import { resolveResourceType } from '@/shared/resourceModuleParity'

import type { GuidanceAction } from './types'

/**
 * O que o builder precisa saber de uma medalha.
 *
 * Estrutural de propósito: `MedalItem` de `useMedalsCollection` satisfaz este
 * formato depois de passar a preservar `missionId`/`journeyId`, e o builder
 * segue sendo função pura, sem depender do hook.
 */
export interface GuidanceMedal {
  id: number
  unlocked: boolean
  journeyId: number | null
  missionId: number | null
}

/** Jornadas e missões do snapshot — só o que resolve o nome da origem. */
export interface MedalOriginCatalog {
  journeys: Array<{ id: number; name: string }>
  missions: Array<{ id: number; name: string }>
}

type OriginKind = 'Journey' | 'Mission'

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1)

const originAction = (
  kind: OriginKind,
  id: number,
  name: string
): GuidanceAction => {
  const resolved = resolveResourceType(kind)

  return {
    key: `medal-origin-${kind.toLowerCase()}-${id}`,
    resourceType: kind,
    // O nome sozinho no título, e o verbo na instrução. Concatenar
    // `completeVerb` + nome produzia "Conclua Visite três estandes" — nomes de
    // missão já costumam ser imperativos, e empilhar dois verbos lê errado.
    title: name,
    instruction: `${capitalize(resolved.completeVerb ?? 'conclua')} ${resolved.label.singular.toLowerCase()} para desbloquear a medalha.`,
    to: resolved.route
  }
}

/**
 * Como conquistar as medalhas que faltam, a partir da origem que o produtor
 * configurou em cada uma.
 *
 * `Medal` exige jornada **ou** missão, e aceita as duas: a validação da api só
 * recusa quando ambas faltam. Medalha com as duas origens rende duas ações —
 * o participante precisa saber dos dois caminhos.
 *
 * Substitui a linha fixa "Complete jornadas para desbloquear medalhas.", que
 * mandava para o lugar errado em todo evento de medalhas por missão.
 */
export const buildMedalsGuidance = (
  medals: GuidanceMedal[],
  { journeys, missions }: MedalOriginCatalog
): GuidanceAction[] => {
  const journeyNames = new Map(journeys.map(item => [item.id, item.name]))
  const missionNames = new Map(missions.map(item => [item.id, item.name]))

  const seen = new Set<string>()
  const actions: GuidanceAction[] = []

  const push = (kind: OriginKind, id: number, name: string | undefined) => {
    // Origem que não resolve no snapshot — jornada removida, snapshot velho —
    // é omitida. A medalha continua na grade; só não gera caminho.
    if (!name) return

    const dedupeKey = `${kind}-${id}`
    if (seen.has(dedupeKey)) return

    seen.add(dedupeKey)
    actions.push(originAction(kind, id, name))
  }

  medals.forEach(medal => {
    if (medal.unlocked) return

    if (medal.journeyId != null) {
      push('Journey', medal.journeyId, journeyNames.get(medal.journeyId))
    }

    if (medal.missionId != null) {
      push('Mission', medal.missionId, missionNames.get(medal.missionId))
    }
  })

  return actions
}
